/**
 * useAttachments — Manages file attachments and per-file metadata state.
 *
 * Extracted from ChatContainer to reduce orchestrator size.
 * Owns: messageFiles, filesMetadata, fileSpecificMetadata, attachments history,
 *       sentFileMetadata (per-file metadata preserved after send),
 *       and the attachments modal open/close state.
 */

import { useState, useCallback } from "react";

export const useAttachments = () => {
  const [messageFiles, setMessageFiles] = useState([]);
  const [filesMetadata, setFilesMetadata] = useState([]);
  const [fileSpecificMetadata, setFileSpecificMetadata] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [sentFileMetadata, setSentFileMetadata] = useState([]);
  const [attachmentsModal, setAttachmentsModal] = useState(false);

  const openAttachmentsModal = useCallback(() => setAttachmentsModal(true), []);
  const closeAttachmentsModal = useCallback(
    () => setAttachmentsModal(false),
    [],
  );

  const handleAttachmentsSave = useCallback((data) => {
    setMessageFiles(data.attachments);
    setFilesMetadata(data.metadata || []);
    setFileSpecificMetadata(data.fileSpecificMetadata || {});
    setAttachmentsModal(false);
  }, []);

  const removeFile = useCallback((index) => {
    setMessageFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const recordSentAttachments = useCallback(
    (messageIndex) => {
      if (messageFiles.length > 0) {
        setAttachments((prev) => [
          ...prev,
          { messageIndex, files: messageFiles },
        ]);

        const hasGlobalMeta = filesMetadata.length > 0;
        const hasSpecificMeta = Object.keys(fileSpecificMetadata).length > 0;

        if (hasGlobalMeta || hasSpecificMeta) {
          // Merge global + file-specific metadata into flat format (matches server response).
          // This is a local fallback for display between send and server response.
          // Once the server responds, metadata_files from the response takes precedence.
          const globalFlat = {};
          filesMetadata.forEach((card) => {
            if (card.key && card.values) {
              globalFlat[card.key] =
                card.values.length === 1 ? card.values[0] : card.values;
            }
          });

          const normalized = {};
          messageFiles.forEach((file) => {
            const merged = { ...globalFlat };
            const cards = fileSpecificMetadata[file.name];
            if (Array.isArray(cards)) {
              cards.forEach((card) => {
                if (card.key && card.values) {
                  merged[card.key] =
                    card.values.length === 1 ? card.values[0] : card.values;
                }
              });
            }
            if (Object.keys(merged).length > 0) {
              normalized[file.name] = merged;
            }
          });

          if (Object.keys(normalized).length > 0) {
            setSentFileMetadata((prev) => [
              ...prev,
              { messageIndex, fileMetadata: normalized },
            ]);
          }
        }
      }
    },
    [messageFiles, filesMetadata, fileSpecificMetadata],
  );

  const clearAfterSend = useCallback(() => {
    setMessageFiles([]);
    setFilesMetadata([]);
    setFileSpecificMetadata({});
  }, []);

  const resetAttachments = useCallback(() => {
    setMessageFiles([]);
    setFilesMetadata([]);
    setFileSpecificMetadata({});
    setAttachments([]);
    setSentFileMetadata([]);
  }, []);

  return {
    messageFiles,
    filesMetadata,
    fileSpecificMetadata,
    attachments,
    sentFileMetadata,
    attachmentsModal,
    openAttachmentsModal,
    closeAttachmentsModal,
    handleAttachmentsSave,
    removeFile,
    recordSentAttachments,
    clearAfterSend,
    resetAttachments,
  };
};
